import { inject, Injectable } from '@angular/core';
import { DeliveryService } from '../../../domains/delivery';
import { Observable, of } from 'rxjs';
import { AnchorDate } from './anchor-date.enum';
import { AnchorDateContext } from './anchor-date.model';

@Injectable({
    providedIn: 'root'
})
export class AnchorDateService {
    private deliveryService = inject(DeliveryService);

    private readonly handlers: Record<AnchorDate, (ctx: AnchorDateContext) => Observable<Date | null>> = {
        [AnchorDate.Today]: () => of(new Date()),
        [AnchorDate.LastDeliveryByDeliveryCenter]: ctx => this.getLastDeliveryDateByDeliveryCenter(ctx.subscriberKey!, ctx.hierarchyNodeKeys ?? []),
        [AnchorDate.LastDeliveryByDivision]: ctx => this.getLastDeliveryDateByDivision(ctx.subscriberKey!, ctx.hierarchyNodeKeys ?? []),
        [AnchorDate.LastServiceCall]: ctx => this.getLastServiceCallDate(ctx.subscriberKey!, ctx.hierarchyNodeKeys ?? []),
        [AnchorDate.LastInvoice]: ctx => this.getInvoiceDate(ctx.subscriberKey!, ctx.hierarchyNodeKeys ?? [])
    }

    /**
     * Resolves the computed anchor date for the given anchor type.
     * 
     * Delegates to the appropriate handler based on the AnchorDate.
     * Returns null if the anchor date cannot be resolved with the provided context.
     * 
     * @param anchorDate The anchor date strategy to resolve. 
     * @param context Context data required for anchor computation.
     * @returns Observable emitting the resolved Date or null
     */
    resolve(anchorDate: AnchorDate, context: AnchorDateContext): Observable<Date | null> {
        const handler = this.handlers[anchorDate];
        return handler ? handler(context) : of(null);
    }

    getLastDeliveryDateByDeliveryCenter(subscriberKey: number, hierarchyNodeKeys: number[]) {
        if (subscriberKey == null) {
            return of(null);
        }
        
        throw Error('Not Implemented');
    }

    getLastDeliveryDateByDivision(subscriberKey: number, hierarchyNodeKeys: number[]) {
        if (subscriberKey == null) {
            return of(null);
        }

        return this.deliveryService.getLastDeliveryDate(subscriberKey, hierarchyNodeKeys);
    }

    getInvoiceDate(subscriberKey: number, hierarchyNodeKeys: number[]) {
        if (subscriberKey == null) {
            return of(null);
        }
        
        throw Error('Not Implemented');
    }

    getLastServiceCallDate(subscriberKey: number, hierarchyNodeKeys: number[]) {
        if (subscriberKey == null) {
            return of(null);
        }
        
        throw Error('Not Implemented');
    }
}
